import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteRule, getAllPaginated1 } from "../../../../services/rules";
import { errorMsg, SORT_DIRECTION } from "../../../../utils/vars";
import {
  setAdvancedSearchHistoryData,
  setAdvancedSearchHistoryFilters,
  setAdvancedSearchHistoryPage,
  setAdvancedSearchHistorySort,
  setAdvancedSearchHistoryTake,
  setAdvancedSearchHistoryTotalCount,
  useSelectFiltersAdvancedSearchHistory,
  useSelectPageAdvancedSearchHistory,
  useSelectSortAdvancedSearchHistory,
  useSelectTakeAdvancedSearchHistory,
  useSelectTotalCountAdvancedSearchHistory,
} from "../../../../redux/features/advancedSearchHistory/advancedSearchHistorySlice";

const useAdvancedSearchHistoryTable = () => {
  const dispatch = useDispatch();
  const sort = useSelectSortAdvancedSearchHistory();
  const page = useSelectPageAdvancedSearchHistory();
  const take = useSelectTakeAdvancedSearchHistory();
  const totalCount = useSelectTotalCountAdvancedSearchHistory();
  const filters = useSelectFiltersAdvancedSearchHistory();

  const queryParams = getQueryParams();

  const {
    data,
    refetch: refetchAdvancedSearchHistory,
    error: errorAdvancedSearchHistoryFetch,
    isPending: isPendingAdvancedSearchHistory,
  } = useQuery({
    queryKey: ["get-advanced-search-history", queryParams],
    queryFn: () => getAllPaginated1(queryParams),
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const deleteRuleMutation = useMutation({
    mutationFn: deleteRule,
    onSuccess: async () => {
      await refetchAdvancedSearchHistory();
      toast.success("Successfully deleted rule");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || errorMsg);
    },
  });

  useEffect(() => {
    if (data) {
      dispatch(setAdvancedSearchHistoryData(data?.data));
      dispatch(setAdvancedSearchHistoryTotalCount(data?.totalCount));
    }
  }, [data, dispatch]);

  const toggleSort = (type) => {
    const currentType = sort.type;
    const currentDirection = sort.direction;

    if (type === currentType) {
      const direction =
        SORT_DIRECTION.DESC === currentDirection
          ? SORT_DIRECTION.ASC
          : SORT_DIRECTION.DESC;
      dispatch(setAdvancedSearchHistorySort({ direction, type }));
    } else {
      dispatch(
        setAdvancedSearchHistorySort({ direction: SORT_DIRECTION.DESC, type }),
      );
    }

    dispatch(setAdvancedSearchHistoryPage(0));
  };

  const handleFilterChange = (key, filter) => {
    if (key !== "DATE") {
      dispatch(setAdvancedSearchHistoryFilters({ [key]: filter }));
    } else {
      dispatch(setAdvancedSearchHistoryFilters({ ...filter }));
    }

    dispatch(setAdvancedSearchHistoryPage(0));
  };

  function getQueryParams() {
    const searchParams = new URLSearchParams();

    searchParams.append("page", page);
    searchParams.append("size", take);
    searchParams.append("direction", sort.direction);
    searchParams.append("type", sort.type);
    searchParams.append("rType", "ADVANCED_SEARCH");

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        searchParams.append(key, value);
      }
    });

    return searchParams.toString();
  }

  const handlePageChange = (newPage) => {
    dispatch(setAdvancedSearchHistoryPage(newPage - 1));
  };

  const handleTakeChange = (newSize) => {
    dispatch(setAdvancedSearchHistoryTake(newSize));
  };

  const deleteHandler = (id) => {
    deleteRuleMutation.mutate(id);
  };

  return {
    handleFilterChange,
    toggleSort,
    deleteHandler,
    handlePageChange,
    handleTakeChange,
    refetchAdvancedSearchHistory,
    isPendingAdvancedSearchHistory,
    errorAdvancedSearchHistoryFetch,
    sort,
    filters,
    take,
    page,
    totalCount,
  };
};

export default useAdvancedSearchHistoryTable;
